import React, { Component, type ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { isAccountInactiveError, isAuthenticationError, isPermissionError } from "@/lib/api-errors";
import { ACCOUNT_INACTIVE_PATH } from "@/lib/auth/session-redirect";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  redirectPath: string | null;
  hasAttemptedRedirect: boolean;
}

export function resolveAuthErrorRedirectPath(error: Error | null): string | null {
  if (error == null) {
    return null;
  }

  if (isAuthenticationError(error)) {
    return "/unauthorized";
  }

  if (isAccountInactiveError(error)) {
    return ACCOUNT_INACTIVE_PATH;
  }

  if (isPermissionError(error)) {
    return "/forbidden";
  }

  return null;
}

interface AuthErrorCard {
  title: string;
  description: string;
  showIcon: boolean;
}

// Card content for each redirect destination from resolveAuthErrorRedirectPath.
const AUTH_ERROR_CARDS: Record<string, AuthErrorCard> = {
  "/unauthorized": { title: "Session Expired", description: "Redirecting to login...", showIcon: true },
  [ACCOUNT_INACTIVE_PATH]: { title: "Account Inactive", description: "Redirecting...", showIcon: false },
  "/forbidden": { title: "Access Denied", description: "Redirecting...", showIcon: true },
};

export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      redirectPath: null,
      hasAttemptedRedirect: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      redirectPath: resolveAuthErrorRedirectPath(error),
      hasAttemptedRedirect: false,
    };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log the error to console
    console.error("Auth Error Boundary caught an error:", error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  override componentDidUpdate(): void {
    const { hasError, redirectPath, hasAttemptedRedirect } = this.state;
    if (!hasError || redirectPath == null || hasAttemptedRedirect) {
      return;
    }

    this.setState({ hasAttemptedRedirect: true }, () => {
      if (typeof window !== "undefined") {
        window.location.replace(redirectPath);
      }
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      redirectPath: null,
      hasAttemptedRedirect: false,
    });
  };

  handleGoHome = (): void => {
    window.location.href = "/";
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      // Auth-state errors show a "redirecting" card while componentDidUpdate
      // performs the navigation to state.redirectPath.
      const card = this.state.redirectPath != null ? AUTH_ERROR_CARDS[this.state.redirectPath] : undefined;
      if (card) {
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
              <CardHeader className="text-center">
                {card.showIcon && (
                  <div className="flex justify-center mb-4">
                    <div className="rounded-full bg-red-100 p-3">
                      <AlertTriangle className="h-10 w-10 text-red-600" />
                    </div>
                  </div>
                )}
                <CardTitle className="text-2xl">{card.title}</CardTitle>
                <CardDescription className="text-base mt-2">{card.description}</CardDescription>
              </CardHeader>
            </Card>
          </div>
        );
      }

      // For non-auth errors, show a generic error boundary
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-yellow-100 p-3">
                  <AlertTriangle className="h-10 w-10 text-yellow-600" />
                </div>
              </div>
              <CardTitle className="text-2xl">Something Went Wrong</CardTitle>
              <CardDescription className="text-base mt-2">
                An unexpected error occurred
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {this.state.error && (
                <div className="bg-gray-100 p-3 rounded-md">
                  <p className="text-sm font-mono text-gray-700 break-all">
                    {this.state.error.message}
                  </p>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Button
                  onClick={this.handleReset}
                  className="w-full bg-[color:var(--rf-color-primary)] hover:bg-[color:var(--rf-color-primary-strong)]"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try Again
                </Button>
                <Button onClick={this.handleGoHome} variant="outline" className="w-full">
                  <Home className="mr-2 h-4 w-4" />
                  Go to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
